import type { Node } from "@xyflow/react";
import type { StratRow } from "../types";

export function band4OnePage(): Node[] {
  const out: Node[] = [];
    // ═══ BAND 4 · THE 1-PAGE MARKETING PLAN (Leo, 2026-07-13) — a study of Allan
    // Dib's nine-square system + what we take for Tailormind. Placed well below the
    // assets band. Layout: two top sliders (the whole strategy · what we take) over a
    // literal 3×3 canvas — three phase rows (Before/During/After) × three squares each.
    const PMP_TITLE_Y = 32000;
    const PMP_COL = 1000;           // x step between the three squares in a row
    const PMP_C2 = 1080;            // the second top slider column
    out.push({ id: "band-1pmp", type: "sectionTitle", position: { x: 0, y: PMP_TITLE_Y },
      data: { title: "The 1-Page Marketing Plan", sub: "Allan Dib's nine-square system — the whole strategy, and what we take for Tailormind", band: true },
      draggable: false, selectable: false });
    out.push({ id: "sec-1pmp", type: "sectionTitle", position: { x: 0, y: 28200 },
      data: { title: "One page, nine squares", sub: "three phases across the customer journey — before · during · after" },
      draggable: false, selectable: false });

    // ── Top-left slider: the whole strategy explained ──
    out.push({
      id: "pmp-overview", type: "section", position: { x: 0, y: 28660 },
      data: {
        eyebrow: "The book", title: "The whole idea",
        description: "Dib replaces the bloated marketing plan with a single page: nine squares you fill in, and you have a complete direct-response system.",
        w: 860,
        slides: [
          { tab: "Thesis", type: "stratCard", data: { label: "Thesis", title: "Marketing on one page, not a hundred",
            lede: "The plan fits on a page because it follows one path: a stranger becomes a lead, a lead becomes a customer, a customer becomes a fan. Nine squares, three phases, done.",
            bullets: [
              "Marketing is the multiplier — a small lift compounds through the whole business",
              "Direct-response over brand: everything is trackable, targeted, and measurable",
              "Built for small teams with no big-brand budget — exactly us",
            ] } },
          { tab: "Three phases", type: "stratCard", data: { label: "The arc", title: "Before · During · After",
            lede: "Every square lives in one of three phases that mirror how a buyer actually moves toward you.",
            rows: [
              { k: "Before", v: "A stranger becomes a lead — target market, message, media" },
              { k: "During", v: "A lead becomes a customer — capture, nurture, convert" },
              { k: "After", v: "A customer becomes a fan — deliver, lifetime value, referrals" },
            ] } },
          { tab: "Five big ideas", type: "stratCard", data: { label: "What carries the book", title: "The five ideas underneath the squares",
            bullets: [
              "Niche hard — an inch wide and a mile deep (score segments with the PVP index)",
              "Speak to emotion; sell an offer, not a product — people justify with logic after",
              "The list / database is your number-one asset",
              "Real profit lives in customer lifetime value, not the first sale",
              "\"Products make you money, systems make you a fortune\"",
            ] } },
        ],
      },
      draggable: false, selectable: false,
    });

    // ── Top-right slider: what we take for Tailormind ──
    out.push({
      id: "pmp-takeaways", type: "section", position: { x: PMP_C2, y: 28660 },
      data: {
        eyebrow: "For Tailormind", title: "What we take",
        description: "Mapped onto our own strategy — what the book confirms we're doing right, and the one gap it exposes.",
        w: 860,
        slides: [
          { tab: "Our niche", type: "stratCard", data: { label: "Square 1 · for us", title: "We already have our inch-wide niche",
            lede: "Dib says dominate one narrow market. Ours is set — mid-market operators (~$40—200M) with no in-house tech team; hunt the leaders (CEO, ops, revenue, CS).",
            rows: [
              { k: "Sharpen", v: "One buyer profile, not five domains — profile over vertical" },
              { k: "Avatar", v: "Pain-aware, solution-unaware — mirror the pain before the pitch" },
            ] } },
          { tab: "Direct response", type: "stratCard", data: { label: "Squares 2—3 · for us", title: "Our content IS the direct response",
            lede: "We can't out-brand anyone and we don't try. The knowledge publication — attributed, save-worthy — is how we earn attention measurably.",
            bullets: [
              "Save + share is the KPI, not impressions",
              "Every piece = one real idea + one beautiful diagram",
              "Measure what actually converts to discovery calls",
            ] } },
          { tab: "The gap: a list", type: "stratCard", data: { label: "Squares 4—5 · our gap", title: "Build the list we don't have yet",
            lede: "Dib's number-one asset is the database, and this is exactly our hole — no capture, no nurture engine today. The clearest thing to fix.",
            bullets: [
              "A lead magnet: a free AI audit / ROI calculator",
              "Capture → nurture the leaders who land, over time",
              "Own the list — it outlasts any single channel",
            ] } },
          { tab: "Ascend value", type: "stratCard", data: { label: "Squares 8—9 · for us", title: "MAX model → growth partnership",
            lede: "Lifetime value is where the profit is, and our ascension ladder is already designed — we just have to run it.",
            rows: [
              { k: "Entry", v: "~$10k MAX-model build — discovery + ~3 automations" },
              { k: "Ascend", v: "Managed growth partnership — retainer + rev-share" },
              { k: "Referrals", v: "Ask MAX + Flame King for peer intros — our warmest leads" },
            ] } },
        ],
      },
      draggable: false, selectable: false,
    });

    // ── The literal nine-square canvas: 3 phase rows × 3 squares ──
    const PMP_GRID_Y = 35260;
    const PMP_ROW = 940;           // vertical pitch between phase rows
    const pmpRow = (i: number) => PMP_GRID_Y + i * PMP_ROW;
    // Phase headers (title node sits ~180 above its row of squares)
    out.push({ id: "pmp-h-before", type: "title", position: { x: 0, y: pmpRow(0) },
      data: { title: "Before", tag: "PROSPECTS → LEADS", ref: true }, draggable: false, selectable: false });
    out.push({ id: "pmp-h-during", type: "title", position: { x: 0, y: pmpRow(1) },
      data: { title: "During", tag: "LEADS → CUSTOMERS", ref: true }, draggable: false, selectable: false });
    out.push({ id: "pmp-h-after", type: "title", position: { x: 0, y: pmpRow(2) },
      data: { title: "After", tag: "CUSTOMERS → FANS", ref: true }, draggable: false, selectable: false });

    const PMP_SQUARES: { row: number; col: number; data: { label: string; title: string; lede?: string; rows?: StratRow[]; bullets?: string[] } }[] = [
      // BEFORE
      { row: 0, col: 0, data: { label: "Before · 1 · Target market", title: "Go an inch wide and a mile deep",
        lede: "Focus limited resources on one laser-narrow niche instead of everyone. A market you can dominate beats a big one you can't.",
        rows: [ { k: "PVP index", v: "Score segments on Personal fulfilment · Value to market · Profitability" }, { k: "Avatar", v: "One ideal customer — their fears, desires, and where they already hang out" } ] } },
      { row: 0, col: 1, data: { label: "Before · 2 · Message", title: "Say one thing they can't ignore",
        lede: "A unique selling proposition plus an irresistible offer. People buy on emotion first, then justify with logic.",
        rows: [ { k: "USP", v: "The honest, specific reason to pick you over the alternatives" }, { k: "Offer", v: "Built around their desire, with the risk reversed" } ] } },
      { row: 0, col: 2, data: { label: "Before · 3 · Media", title: "Rent attention where the avatar is",
        lede: "Choose channels by ROI, not reach. Prefer direct-response — trackable, targeted, measurable — over brand ads.",
        rows: [ { k: "Metrics", v: "Know your acquisition cost and lifetime value before you spend" }, { k: "Rule", v: "Test small, then scale only the winners" } ] } },
      // DURING
      { row: 1, col: 0, data: { label: "During · 4 · Lead capture", title: "Capture the interested, don't chase the sale",
        lede: "Most prospects aren't ready to buy. Trade value for their details and build the database — the real asset.",
        bullets: [ "An ethical bribe: a guide, tool, or audit worth opting in for", "A clear reason to hand over the email", "The list is the asset you actually own" ] } },
      { row: 1, col: 1, data: { label: "During · 5 · Nurture", title: "Farming, not hunting",
        lede: "Deliver value over time until they're ready. Many touches, automated, so you stay top-of-mind without pushing.",
        rows: [ { k: "Cadence", v: "Show up consistently with genuinely useful content" }, { k: "Tools", v: "Marketing automation does the patient follow-up for you" } ] } },
      { row: 1, col: 2, data: { label: "During · 6 · Convert", title: "Be the expert they ask to buy from",
        lede: "Position, don't pressure. Trust plus risk reversal turns interested leads into a first purchase.",
        bullets: [ "Demonstrate expertise and proof", "Reverse the risk — guarantee or trial", "Price with confidence; anchor on value" ] } },
      // AFTER
      { row: 2, col: 0, data: { label: "After · 7 · Deliver", title: "Turn customers into raving fans",
        lede: "A remarkable experience is itself marketing. Systematise the 'wow' so it happens every time and people talk.",
        bullets: [ "Deliver results, not just a product", "Build systems so quality is repeatable", "Surprise and delight on purpose" ] } },
      { row: 2, col: 1, data: { label: "After · 8 · Lifetime value", title: "The profit is in the second sale",
        lede: "It's far cheaper to sell more to existing customers than to win new ones. Raise frequency, price, and ascension.",
        rows: [ { k: "Levers", v: "Raise prices · upsell · reactivate · resell · reduce churn" }, { k: "Fire", v: "Drop the customers who drain more than they pay" } ] } },
      { row: 2, col: 2, data: { label: "After · 9 · Referrals", title: "Engineer word-of-mouth, don't wait",
        lede: "Referrals are a system you run, not luck you hope for. Ask deliberately and partner with adjacent businesses.",
        bullets: [ "Ask at the moment of delight", "Make referring easy and rewarding", "Partner with who already serves your avatar" ] } },
    ];
    PMP_SQUARES.forEach((sq) => {
      out.push({
        id: `pmp-sq-${sq.row}-${sq.col}`, type: "stratCard",
        position: { x: sq.col * PMP_COL, y: pmpRow(sq.row) + 180 },
        data: sq.data, draggable: false, selectable: false,
      });
    });

  return out;
}
